import {
  Box,
  Button,
  Checkbox,
  Paper,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { fallback } from "components/screen/phase-3/riskAdmin/RiskTabs";
import { NoDataComponent } from "components/widgets/phase-3/esgMaturity/shareSurvey/NoData";
import stirrupLogo from "images/phase-3/stirrup_logo/stirrup-logo.svg";
import noData from "images/no_data_images/survey_section.svg";
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import apiBolerplate from "utils/_helpers/apiBolerplate";
import { TagBox } from "../supplier/supplierViewTabs/right/SupplierDetails";
import { NoDataSurveyCompleted } from "./shareSurvey/NoDataSurveyCompleted";

const sections = [
  "General Company Information",
  "ESG Reporting & Data Management",
  "Environment",
  "Social",
  "Governance",
];

const MaturitySurvey = () => {
  const { org_id: organization_id, id: maturity_assessment_id } = useParams();
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [otherSector, setOtherSectors] = useState("");

  const scrollRef = useRef(null);
  // const [loadingSubmit, setLoadingSubmit] = useState(false);

  const { data: questions = [], isLoading } = useQuery({
    queryKey: ["maturity"],
    queryFn: async () => {
      const res = await apiBolerplate({
        url: "/esg_maturity/maturity_assessment/fetch_maturity_assessment_questions",
        bodyData: { organization_id, maturity_assessment_id },
        hideMsg: true,
        hideLoader: true,
      });
      setIsSubmitted(res?.is_submitted);
      setOtherSectors(res?.other_responses);
      return res?.status ? res.result : [];
    },
    staleTime: Infinity,
    cacheTime: Infinity,
  });

  const [selectedSection, setSelectedSection] = useState(sections[0]);
  const [answers, setAnswers] = useState({});
  const currentIndex = sections.indexOf(selectedSection);

  const groupedData = useMemo(() => {
    return questions.reduce((acc, q) => {
      const cat1 = q.category1 || "General";
      const cat2 = q.category2 || "General";
      (acc[cat1] ||= {})[cat2] = [...(acc[cat1][cat2] || []), q];
      return acc;
    }, {});
  }, [questions]);

  const handleChange = (id, type, value, isOther = false) =>
    setAnswers((prev) => {
      if (isOther) {
        return { ...prev, [`${id}_other`]: value };
      }

      if (type === "SINGLE_CHOICE") {
        return { ...prev, [id]: value };
      }

      const current = Array.isArray(prev[id]) ? [...prev[id]] : [];
      const index = current.indexOf(value);
      if (index !== -1) {
        current.splice(index, 1);
      } else {
        current.push(value);
      }
      return { ...prev, [id]: current };
    });

  const handleSubmit = async (isFinal = false) => {
    
    // setLoadingSubmit(true);
    // try {
    const res = await apiBolerplate({
      // url: "maturity_matrix/submit_maturity_matrix_shared_survey",
      url: "/esg_maturity/maturity_assessment/submit_maturity_assessment",
      bodyData: {
        organization_id,
        submission: answers,
        is_submitted: isFinal,
        maturity_assessment_id,
      },
      messageType: "success",
      hideLoader: !isFinal,
    });
    if (res?.status && isFinal) {
      setIsSubmitted(true);
      localStorage.removeItem("selectedSector");
    }
    // } finally {
    //   setLoadingSubmit(false);
    // }
  };

  const selectedSector = useMemo(() => {
    // Step 1: Pehle localStorage me saved sector dekho
    const savedSector = localStorage.getItem("selectedSector");
    if (savedSector) return savedSector;

    // Step 2: Agar localStorage me nahi hai to answer se lo
    const q = questions.find(
      (x) =>
        x.question ===
        "Which sector does your organisation predominantly operate in?"
    );
    //  const getOption = q?.option.find(o => o[1])?.[0]
    return q ? answers[q._id] : null;
  }, [answers, questions]);

  const visibleCategories = groupedData[selectedSection]
    ? Object.entries(groupedData[selectedSection]).filter(([_, list]) =>
        list.some(
          (q) =>
            !q.applicable_sectors?.length ||
            !selectedSector ||
            q.applicable_sectors.includes(selectedSector)
        )
      )
    : [];

  const visibleQuestions = useMemo(() => {
    return visibleCategories.flatMap(([_, list]) =>
      list.filter(
        (q) =>
          !q.applicable_sectors?.length ||
          !selectedSector ||
          q.applicable_sectors.includes(selectedSector)
      )
    );
  }, [visibleCategories, selectedSector]);

  const allAnswered = visibleQuestions.every((q) => {
    const val = answers[q._id];
    if (q.question_type === "SINGLE_CHOICE") return !!val;
    if (q.question_type === "MULTIPLE_CHOICE") return val?.length > 0;

    return false;
  });

  // const canProceed = allAnswered;

  useEffect(() => {
    // Find the sector question
    const q = questions.find(
      (x) =>
        x.question ===
        "Which sector does your organisation predominantly operate in?"
    );

    // Agar user ne sector select kar liya ho to usse localStorage me save karo
    if (q && answers[q._id]) {
      localStorage.setItem("selectedSector", answers[q._id]);
    }
  }, [answers, questions]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedSection]);

  if (isSubmitted) {
    return (
      <Box mt="100px">
        <NoDataSurveyCompleted
          mainTitle="Maturity Assessment is completed"
          // subTitle={`The survey ended. You can no longer submit responses.`}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: "#fff" }}>
      <Paper
        sx={{ display: "flex", justifyContent: "flex-start", p: 1, px: 3.5 }}
      >
        <img
          src={stirrupLogo}
          alt="logo"
          style={{ height: 45, objectFit: "cover", marginLeft: "20px" }}
        />
      </Paper>

      <Box sx={{ px: 4, pb: 2 }}>
        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.5,
          }}
        >
          <Typography variant="h3" fontWeight={600}>
            Maturity Survey
          </Typography>
        </Box> */}

        <Paper
          elevation={0}
          sx={{
            display: "flex",
            borderRadius: "5px",
            mt: 2,
            mb: 1.5,
            height: "calc(100vh - 150px)",
            boxShadow: (t) =>
              `3px -3px 8px -3px ${t.palette.primary.main}25,
               -3px 3px 8px -3px ${t.palette.primary.main}25,
               0px 6px 16px -4px ${t.palette.primary.main}40`,
          }}
        >
          {/* Content Side */}
          <Box ref={scrollRef} sx={{ width: "100%", overflowY: "auto" }}>
            {/* Section Header */}
            <Box
              sx={{
                backgroundColor: "#fff",
                p: "16px",
                position: "sticky",
                top: 0,
                zIndex: 1,
              }}
            >
              <Typography
                variant="h3"
                fontWeight={600}
                sx={{ color: "#000", mb: 1 }}
              >
                {selectedSection}
              </Typography>
              <Box
                sx={{
                  border: "1px solid #E9E9E9",
                  backgroundColor: "#fff",
                  borderRadius: "4px",
                  px: 1.5,
                  py: 0.5,
                  display: "inline-block",
                }}
              >
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 500,
                    color: "#000",
                  }}
                >
                  Section {currentIndex + 1} of {sections.length}
                </Typography>
              </Box>
            </Box>
            {isLoading ? (
              fallback({ showFooter: false })
            ) : visibleCategories.length === 0 ? (
              <Box sx={{ mt: "180px" }}>
                <NoDataComponent
                  imgSrc={noData}
                  mainTitle="No questions found in this section"
                  subTitle="Try selecting another section or check your selected sector"
                  customHeight="360px"
                  customWidth="400px"
                />
              </Box>
            ) : (
              visibleCategories.map(([cat2, list]) => (
                <Box key={cat2}>
                  {/* Sub-category header */}
                  <Box sx={{ px: 0 }}>
                    <Box
                      sx={{
                        border: "1px solid #E9E9E9",
                        backgroundColor: "#fdecea",
                        px: 2,
                        py: 1.5,
                        width: "100%",
                        mb: 2,
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: 16,
                          fontWeight: 500,
                          color: "#000",
                        }}
                      >
                        {cat2}
                      </Typography>
                    </Box>
                  </Box>
                  {list
                    .filter(
                      (q) =>
                        !q.applicable_sectors?.length ||
                        !selectedSector ||
                        q.applicable_sectors.includes(selectedSector)
                    )
                    .map((q, qIndex) => {
                      const filteredList = list.filter(
                        (item) =>
                          !item.applicable_sectors?.length ||
                          !selectedSector ||
                          item.applicable_sectors.includes(selectedSector)
                      );
                      const globalQuestionIndex = filteredList.indexOf(q);
                      return (
                        <Box
                          key={q._id}
                          sx={{ p: "16px", borderBottom: "1px solid #E9E9E9" }}
                        >
                          <Box
                            sx={{
                              border: "1px solid #E9E9E9",
                              backgroundColor: "#fdecea",
                              borderRadius: "4px",
                              px: 1.5,
                              py: 0.5,
                              mb: 2,
                              display: "inline-block",
                            }}
                          >
                            <Typography
                              style={{
                                fontSize: 14,
                                fontWeight: 500,
                                color: "#000",
                              }}
                            >
                              Question: {globalQuestionIndex + 1} of{" "}
                              {filteredList.length}
                            </Typography>
                          </Box>
                          <Box sx={{ mb: 2 }}>
                            <Typography
                              sx={{
                                fontSize: 14,
                                p: "8px",
                                border: "1px solid #E9E9E9",
                                backgroundColor: "#FCFCFC",
                                borderRadius: "6px",
                                width: "100%",
                              }}
                            >
                              {q.question}
                            </Typography>
                          </Box>

                          {q.question_type === "SINGLE_CHOICE" ? (
                            <RadioGroup
                              value={answers[q._id] || ""}
                              onChange={(e) =>
                                handleChange(
                                  q._id,
                                  q.question_type,
                                  e.target.value
                                )
                              }
                            >
                              {q.option?.map(([label, isSelected], i) => {
                                const isChecked =
                                  answers[q._id] === label ||
                                  (!answers[q._id] && isSelected === true);
                                return (
                                  <Box key={i} sx={{ mb: 1 }}>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Radio
                                        size="small"
                                        value={label}
                                        checked={isChecked}
                                        onChange={(e) =>
                                          handleChange(
                                            q._id,
                                            q.question_type,
                                            e.target.value
                                          )
                                        }
                                      />
                                      <Typography>{label}</Typography>
                                    </Box>
                                    {label === "Other (please specify)" &&
                                      isChecked && (
                                        <TextField
                                          size="small"
                                          variant="outlined"
                                          placeholder="Please specify..."
                                          value={
                                            answers[`${q._id}_other`] ||
                                            otherSector?.[q._id] ||
                                            ""
                                          }
                                          onChange={(e) =>
                                            handleChange(
                                              q._id,
                                              q.question_type,
                                              e.target.value,
                                              true
                                            )
                                          }
                                          sx={{ mt: 1, ml: 4, width: "60%" }}
                                        />
                                      )}
                                  </Box>
                                );
                              })}
                            </RadioGroup>
                          ) : (
                            q.option?.map(([label, isSelected], i) => {
                              const checked =
                                (answers[q._id] || []).includes(label) ||
                                (!answers[q._id]?.length &&
                                  isSelected === true);
                              return (
                                <Box key={i} sx={{ mb: 1 }}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                    }}
                                  >
                                    <Checkbox
                                      size="small"
                                      checked={checked}
                                      onChange={() =>
                                        handleChange(
                                          q._id,
                                          q.question_type,
                                          label
                                        )
                                      }
                                    />
                                    <Typography>{label}</Typography>
                                  </Box>
                                  {label === "Other (please specify)" &&
                                    checked && (
                                      <TextField
                                        size="small"
                                        variant="outlined"
                                        placeholder="Please specify..."
                                        value={answers[`${q._id}_other`] || ""}
                                        onChange={(e) =>
                                          handleChange(
                                            q._id,
                                            q.question_type,
                                            e.target.value,
                                            true
                                          )
                                        }
                                        sx={{ mt: 1, ml: 4, width: "60%" }}
                                      />
                                    )}
                                </Box>
                              );
                            })
                          )}
                        </Box>
                      );
                    })}
                </Box>
              ))
            )}
          </Box>
        </Paper>

        <Box
          sx={{ display: "flex", justifyContent: "flex-end", mt: 2, gap: 1 }}
        >
          <Button
            variant="text"
            disabled={currentIndex === 0}
            onClick={() => setSelectedSection(sections[currentIndex - 1])}
            sx={{
              color: "#000",
              "&:hover": {
                backgroundColor: "#fdecea",
              },
              "&:disabled": {
                color: "rgba(0, 0, 0, 0.26)",
              },
            }}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            disabled={
              currentIndex === sections.length - 1 ? !allAnswered : false
            }
            onClick={() =>
              currentIndex === sections.length - 1
                ? handleSubmit(true)
                : (handleSubmit(false),
                  setSelectedSection(sections[currentIndex + 1]))
            }
            sx={{
              backgroundColor: "#ef412a",
              "&:hover": {
                backgroundColor: "#d63820",
              },
            }}
          >
            {currentIndex === sections.length - 1
              ? "Submit Survey"
              : "Save Section & Proceed to next"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default MaturitySurvey;
